import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

/**
 * Application-wide display timezone. Defaults to Asia/Kolkata (IST) because the
 * primary user base is in India.
 *
 * Override in tests or for per-user preferences by re-providing this token at
 * a higher level in the injector tree, e.g.:
 *
 *   { provide: APP_TIME_ZONE, useValue: 'America/Los_Angeles' }
 */
export const APP_TIME_ZONE = new InjectionToken<string>('APP_TIME_ZONE', {
  providedIn: 'root',
  factory: () => 'Asia/Kolkata',
});

export type FormatStyle = 'time' | 'date' | 'datetime' | 'relative' | 'iso';

/**
 * Central date-time helper for the app. Handles:
 *   - Parsing naive ISO strings as UTC (the convention our backends use).
 *   - Displaying values in a single configured timezone regardless of the
 *     viewer's device locale, so a doctor's appointment "1:30 PM" reads the
 *     same on a Mumbai phone, a Singapore laptop, and a London server log.
 *   - Relative-time phrases ("3m ago", "Yesterday", "2 days ago").
 *
 * Implementation uses native `Intl.DateTimeFormat` — no additional packages,
 * works in every browser the rest of the app targets. The service surface is
 * intentionally minimal so call sites stay readable.
 */
@Injectable({ providedIn: 'root' })
export class DateTimeService {
  /** Memoised formatters — re-creating an Intl.DateTimeFormat per call is the
   *  single most-expensive thing in this file. Cache by (style, locale, tz). */
  private readonly formatterCache = new Map<string, Intl.DateTimeFormat>();

  constructor(
    @Optional() @Inject(APP_TIME_ZONE) private readonly timeZone: string,
  ) {
    if (!this.timeZone) { this.timeZone = 'Asia/Kolkata'; }
  }

  /** Current configured display timezone (IANA name). */
  get zone(): string { return this.timeZone; }

  /**
   * Parse a value that may be either a Date, an ISO string with explicit
   * timezone, or a naive ISO string. Naive strings are interpreted as UTC —
   * the convention our backends use for `created_at` / `last_active` etc.
   *
   * Returns an absolute Date so consumers can format it in any zone.
   * Invalid inputs return `new Date(NaN)`; callers should check `isNaN(d)`
   * if they need to branch on validity.
   */
  parse(value: Date | string | null | undefined): Date {
    if (!value) { return new Date(Number.NaN); }
    if (value instanceof Date) { return value; }
    const hasExplicitOffset = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(value);
    return new Date(hasExplicitOffset ? value : value + 'Z');
  }

  /** "1:27 AM" in the configured zone. */
  formatTime(value: Date | string | null | undefined): string {
    const d = this.parse(value);
    if (Number.isNaN(d.getTime())) { return ''; }
    return this.formatter('time').format(d);
  }

  /** "13 May 2026" by default; pass a custom style for variants. */
  formatDate(value: Date | string | null | undefined): string {
    const d = this.parse(value);
    if (Number.isNaN(d.getTime())) { return ''; }
    return this.formatter('date').format(d);
  }

  /** "13 May 2026, 1:27 AM" combined display. */
  formatDateTime(value: Date | string | null | undefined): string {
    const d = this.parse(value);
    if (Number.isNaN(d.getTime())) { return ''; }
    return this.formatter('datetime').format(d);
  }

  /**
   * Human relative phrase keyed off `Date.now()`. Returns timezone-independent
   * results — "3 hours ago" is true everywhere in the world simultaneously —
   * but the >7-day fallback formats the date in the configured display zone.
   */
  formatRelative(value: Date | string | null | undefined): string {
    const d = this.parse(value);
    if (Number.isNaN(d.getTime())) { return ''; }
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) { return 'just now'; }
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) { return 'just now'; }
    if (minutes < 60) { return `${minutes}m ago`; }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) { return `${hours}h ago`; }
    const days = Math.floor(hours / 24);
    if (days === 1) { return 'Yesterday'; }
    if (days < 7) { return `${days}d ago`; }
    const weeks = Math.floor(days / 7);
    if (weeks < 4) { return `${weeks}w ago`; }
    const months = Math.floor(days / 30);
    if (months < 12) { return `${months}mo ago`; }
    return this.formatDate(d);
  }

  /** ISO string of the parsed value (always UTC). Handy for cache keys. */
  toIsoUtc(value: Date | string | null | undefined): string {
    const d = this.parse(value);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }

  private formatter(style: FormatStyle): Intl.DateTimeFormat {
    const locale = 'en-IN';
    const cacheKey = `${style}|${locale}|${this.timeZone}`;
    const cached = this.formatterCache.get(cacheKey);
    if (cached) { return cached; }
    const opts: Intl.DateTimeFormatOptions = { timeZone: this.timeZone };
    switch (style) {
      case 'time':
        opts.hour = 'numeric';
        opts.minute = '2-digit';
        opts.hour12 = true;
        break;
      case 'date':
        opts.day = '2-digit';
        opts.month = 'short';
        opts.year = 'numeric';
        break;
      case 'datetime':
        opts.day = '2-digit';
        opts.month = 'short';
        opts.year = 'numeric';
        opts.hour = 'numeric';
        opts.minute = '2-digit';
        opts.hour12 = true;
        break;
      default:
        opts.dateStyle = 'medium';
    }
    const created = new Intl.DateTimeFormat(locale, opts);
    this.formatterCache.set(cacheKey, created);
    return created;
  }
}
